import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Typography, Box, TextField, Button, Divider, Alert, Chip } from "@mui/material";
import { ArrowBack, TableRestaurant } from "@mui/icons-material";
import { useSession } from "../context/SessionContext";
import { useOrder } from "../context/OrderContext";
import { formatPrice, orderTotal } from "../utils/helpers";
import OrderLineCard from "../components/OrderLineCard";

// The pre-placement cart — order status 'created'. No order-type toggle and
// no table picker here: this app only ever places dine_in orders, and the
// table comes from the QR code already scanned (SessionContext), not from a
// choice made on this screen.
export default function Cart() {
  const navigate = useNavigate();
  const { currency, tableKey, table, contactNumber, customerName } = useSession();
  const { order, submitOrder, submitting, updateItemQuantity, removeItem } = useOrder();

  const [name, setName] = useState(customerName);
  const [contact, setContact] = useState(contactNumber);
  const [orderNote, setOrderNote] = useState(order?.order_note || "");
  const [error, setError] = useState("");
  const [busyItemId, setBusyItemId] = useState(null);

  const items = order?.order_items || [];

  const handleQuantityChange = async (item, quantity) => {
    setBusyItemId(item.id);
    try {
      await updateItemQuantity(item, quantity);
    } catch (err) {
      setError(err.message || "Failed to update item");
    } finally {
      setBusyItemId(null);
    }
  };

  const handleRemove = async (item) => {
    setBusyItemId(item.id);
    try {
      await removeItem(item);
    } catch (err) {
      setError(err.message || "Failed to remove item");
    } finally {
      setBusyItemId(null);
    }
  };

  const handlePlaceOrder = async () => {
    setError("");
    try {
      await submitOrder({ name, contact, orderNote });
      navigate("/live-order", { replace: true });
    } catch (err) {
      setError(err.message || "Failed to place order");
    }
  };

  return (
    <Box sx={{ pb: 20 }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            Your Order
          </Typography>
          {tableKey && <Chip icon={<TableRestaurant fontSize="small" />} label={`Table ${table?.name || tableKey}`} color="primary" size="small" />}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {items.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            Your cart is empty.
          </Typography>
        ) : (
          items.map((item) => (
            <OrderLineCard
              key={item.id}
              item={item}
              editable
              onQuantityChange={handleQuantityChange}
              onRemove={handleRemove}
              busy={busyItemId === item.id}
            />
          ))
        )}

        {items.length > 0 && (
          <>
            <TextField
              label="Your name (optional)"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mt: 1, mb: 1.5 }}
            />
            <TextField
              label="Contact number (optional)"
              type="tel"
              fullWidth
              size="small"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="Order note (optional)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
            />
          </>
        )}
      </Box>

      {items.length > 0 && (
        <Box sx={{ position: "fixed", left: 0, right: 0, bottom: 0, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", p: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="h6">Total</Typography>
            <Typography variant="h6" fontWeight={800}>
              {formatPrice(orderTotal(order), currency)}
            </Typography>
          </Box>
          <Button fullWidth variant="contained" size="large" disabled={submitting} onClick={handlePlaceOrder} sx={{ py: 1.4 }}>
            {submitting ? "Placing order..." : "Place Order"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
